// src/screens/ProductScreen.jsx
import { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetProductDetailsQuery,
  useGetProductsQuery,
} from '../../slices/productsApiSlice';
import { LinkContainer } from 'react-router-bootstrap';
import GalleryDisplay from '../../components/GalleryDisplay';
import ImageGallery from '../../components/ImageGallery';
import ShopMessage from '../../components/ShopMessage';
import SelectBuyPopup from '../../components/SelectBuyPopup';
import SelectRentalPopup from '../../components/SelectRentalPopup';
import FavoriteButton from '../../components/FavoriteButton';
import ProductpageFooter from '../../components/ProductpageFooter';
import { addToCart } from '../../slices/cartSlice';
import { trackAddToCart } from '../../utils/gaAnalytics';
import { COLOR, SIZE, GENDER } from '../../constants/productConstants';
import Breadcrumb from '../../components/SEO/Breadcrumb';
import { useJsonLd } from '../../context/JsonLdContext';
import {
  FRONTEND_BASE_URL,
  RENTAL_RATE_NON_ORG,
  RENTAL_RATE_ORG,
  SHIPPING_FEE,
} from '../../constants/constants';

const ProductScreen = () => {
  const { id: productId } = useParams();
  const { registerFragment } = useJsonLd();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─────────────────── 状態 ─────────────────── */
  const [serviceOption, setServiceOption] = useState('rent');
  const [useSubscription, setUseSubscription] = useState(true);
  const [rentalPeriod, setRentalPeriod] = useState('4days');
  const [inStore, setInStore] = useState(true);

  // ───────── SHOP MESSAGE 用 ─────────
  

  /* ─────────────────── API ─────────────────── */
  const {
    data: product,
    isLoading,
    refetch,
    error,
  } = useGetProductDetailsQuery(productId);

  const { data: productListData, isLoading: listLoading } =
    useGetProductsQuery({});
  const allProducts = productListData?.products ?? [];

  /* ─────────────────── 関連商品 ─────────────────── */
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts.filter(
      (p) => p.shopName === product.shopName && p.id !== product.id,
    );
  }, [allProducts, product]);

  const sameCategoryProducts = useMemo(() => {
    if (!product?.parentCategory?.id) return [];
    const targetId = product.parentCategory.id;
    return allProducts.filter(
      (p) => p.parentCategory?.id === targetId && p.id !== product.id,
    );
  }, [allProducts, product?.parentCategory?.id, product?.id]);

  /* ─────────────────── 認証情報 ─────────────────── */
  const { userInfo } = useSelector((state) => state.auth);
  const canUseSubscription =
    userInfo &&
    userInfo.subscription &&
    userInfo.subscription.usedItems < userInfo.subscription.maxItems &&
    product?.price <= userInfo.subscription.itemPriceLimit;

  /* ─────────────────── 料金計算 ─────────────────── */
  const calcBasePrice = ({
    price = 0,
    serviceOption,
    useSubscription,
    canUseSubscription,
    isOrgUser,
    rentalPeriod,
  }) => {
    if (serviceOption !== 'rent') return price; // 購入

    if (useSubscription && canUseSubscription) return 0; // サブスク

    // 単品レンタル
    const table = isOrgUser ? RENTAL_RATE_ORG : RENTAL_RATE_NON_ORG;
    const rate = table[rentalPeriod] ?? table.default;
    return Math.round(price * rate);
  };

  /** レンタル料 or 購入料金 */
  const basePrice = useMemo(
    () =>
      calcBasePrice({
        price: Number(product?.price) || 0,
        serviceOption,
        useSubscription,
        canUseSubscription,
        isOrgUser: !!userInfo?.organization,
        rentalPeriod,
      }),
    [
      product?.price,
      serviceOption,
      useSubscription,
      canUseSubscription,
      userInfo?.organization,
      rentalPeriod,
    ],
  );

  /** 送料 */
  const shippingCost = useMemo(() => (inStore ? 0 : SHIPPING_FEE), [inStore]);

  /** 最終支払額（送料込み） */
  const finalCost = useMemo(() => basePrice + shippingCost, [basePrice, shippingCost]);

  /* ─────────────────── カート投入 ─────────────────── */
  const addToCartHandler = () => {
    const itemPayload = {
      ...product,
      serviceOption,
      inStore,
      useSubscription: canUseSubscription && useSubscription,
      rentalPeriod: !useSubscription ? rentalPeriod : 'unlimited',
      finalPrice: finalCost, // 送料込み
      shippingCost,          // 内訳保持
    };

    dispatch(addToCart(itemPayload));
    trackAddToCart(itemPayload);
    navigate('/cart');
  };

  /* ─────────────────── その他副作用 ─────────────────── */
  useEffect(() => {
    if (product && product.status !== 'available') {
      setServiceOption('buy');
    }
  }, [product]);

  /* ─────────────────── パンくず ─────────────────── */
  const items = useMemo(
    () => [
      { name: 'ふるぷら', url: '/' },
      {
        name: product?.shopName || 'Shop',
        url: `/shop/${product?.Shop.slug}`,
      },
      {
        name: product?.parentCategory?.name || 'Category',
        url: `/category/${product?.parentCategory?.slug}`,
      },
      {
        name: product?.category?.name || 'Category',
        url: `/category/${product?.parentCategory?.slug}/${product?.category?.slug}`,
      },
      { name: product?.name || 'Item', url: '' },
    ],
    [product],
  );

  /* ─────────────────── JSON‑LD ─────────────────── */
  function buildPeopleAudience(genderId) {
    const audienceBlock = { '@type': 'PeopleAudience' };
    switch (genderId) {
      case 1:
        audienceBlock.suggestedGender = 'Unisex';
        break;
      case 2:
        audienceBlock.suggestedGender = 'https://schema.org/Male';
        break;
      case 3:
        audienceBlock.suggestedGender = 'https://schema.org/Female';
        break;
      case 4:
        audienceBlock.suggestedGender = 'Unisex';
        audienceBlock.suggestedMinAge = '3';
        audienceBlock.suggestedMaxAge = '12';
        break;
      default:
        audienceBlock.suggestedGender = 'Unisex';
    }
    return audienceBlock;
  }

 /* ─────────────────── レンタル・購入ボタンクリック後詳細計算の表示 ─────────────────── */
 
 useEffect(() => {
    if (product) {
      const colorName = product.color ? COLOR[product.color] : null;
      const sizeName = product.size ? SIZE[product.size] : null;
      const audienceData = buildPeopleAudience(product.gender);

      registerFragment({
        '@type': 'Product',
        name: product.name,
        image: [product.image, ...(product.subImages || [])],
        description: product.description,
        brand: { '@type': 'Brand', name: product.shopName },
        ...(colorName && { color: colorName }),
        ...(sizeName && { size: sizeName }),
        audience: audienceData,
        offers: {
          '@type': 'Offer',
          url: window.location.href,
          price: product.price,
          priceCurrency: 'JPY',
          itemCondition: 'https://schema.org/UsedCondition',
          availability:
            product.status === 'available' ? 'InStock' : 'OutOfStock',
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: SHIPPING_FEE,
              currency: 'JPY',
            },
            shippingLabel: 'Delivery',
          },
          hasMerchantReturnPolicy: true,
          returnPolicy: {
            '@type': 'MerchantReturnPolicy',
            merchantReturnDays: 8,
            returnPolicyCategory: 'https://schema.org/NonReturnable',
            description:
              '誤配送や明らかに実際の商品と記載内容に差異がある場合のみ、8日以内の返品ができます。',
            url: `${FRONTEND_BASE_URL}/help/cancellation-return-policy`,
          },
        },
      });
    }
  }, [product, registerFragment]);

  if (!product) return <div>Loading...</div>;
  /* ---------- 商品画像整列 ---------- */

  const images = [product.image, ...(product.subImages || [])];


  /* ─────────────────── JSX ─────────────────── */
  return (
    <div
      className="productpage"
      style={{
        backgroundImage: `url('https://furupura-assets.s3.ap-northeast-1.amazonaws.com/samples/backgrounds/background.webp')`,
        height: '100%',
        width: '100%',
        backgroundRepeat: 'repeat',
        overflow: 'hidden',
      }}
    >
      <div className="productpage-breadcrumb">
        <Breadcrumb items={items} />
      </div>

      <div className="productpage-row">
        {/* 画像エリア */}
        <div className="productpage-image-column">
          <ImageGallery images={images} />
          {/* 関連商品カルーセル */}
          {relatedProducts.length > 0 && (
            <div className="productpage-image-column-relateditem">
              <p className="productpage-image-column-relateditem-subtitle">
                <Link
                  to={`/shop/${product.Shop.slug}`}
                  >{product?.shopName}
                </Link>
                の商品
              </p>
              <h2 className="productpage-image-column-relateditem-title">
                Other Item
              </h2>
              <GalleryDisplay type="shop" products={relatedProducts} />
            </div>
          )}

          {sameCategoryProducts.length > 0 && (
            <div className="productpage-image-column-relateditem">
              <p className="productpage-image-column-relateditem-subtitle">
                同じカテゴリーの商品
              </p>
              <h2 className="productpage-image-column-relateditem-title">
                新着{product?.parentCategory?.name}
              </h2>
              <GalleryDisplay type="shop" products={sameCategoryProducts} />
            </div>
          )}
        </div>

        {/* 商品情報エリア */}
        <div className="productpage-info-column">
          <p className="productpage-label">{product.Tags?.map((tag) => tag.name).join(' / ')}</p>
          <h3 className="productpage-name">{product.name}</h3>
          <p className="productpage-shop">{product?.shopName}</p>
          <div className="productpage-pricebox">
            <span className="productpage-pricebox-price">
            ¥{product.price.toLocaleString()}
            <span className="productpage-pricebox-price-note">（定価税込）</span>
            </span>
            {/* お気に入り */}
            <div className="productpage-pricebox-favorite">
              <FavoriteButton itemId={product.id} isProduct={true}
              className="productpage-pricebox-favorite-button" />
            </div>
          </div>
          <p className="productpage-shipping">
            店舗受取であれば配送{SHIPPING_FEE.toLocaleString()}円が無料
          </p>
          <p className="productpage-stock">購入可能商品</p>
          <p className="productpage-stock">洗濯可能/返却時の洗濯は必須ではありません</p>

           {/* 商品購入ボタン */}
          <SelectRentalPopup
            product={product} style={{marginBottom:'1vw'}}/>
          <SelectBuyPopup
            product={product}/>

          {/* SHOP MESSAGE */}
          <div className="productpage-shopmessage">
            <ShopMessage product={product} />
          </div>
    
  
          {/* 商品詳細テーブル */}
          <div className="productpage-product-detail-table">
            <div className="productpage-product-detail-table-title">
              <p>サイズ</p>
              <p>色</p>
              <p>状態</p>
              <p>性別</p>
              <p>素材</p>
              <p>発送日</p>
            </div>
            <div className="productpage-product-detail-table-value">
              <p>{SIZE[product.size] ?? 'サイズ不明'}</p>
              <p>{COLOR[product.color] ?? 'カラー不明'}</p>
              <p>{product.condition}</p>
              <p>{GENDER[product.gender] ?? '不明'}</p>
              <p>{product.material}</p>
              <p>ご注文から3営業日以内</p>
            </div>
          </div>
        </div>
      </div>
      <ProductpageFooter product={product} />
    </div>
  );
};
export default ProductScreen;
